/**
 * What the Home page shows (firm-model spec §10.2), derived purely from /v1/me so the
 * rules are tested without rendering.
 */
import { PROVISIONED_FIRM_NAME, type Me } from "@contract/firm/me";
import type { FirmStandards } from "@contract/standards/standards";

/** Share of the pool used at which Home nudges (spec: "at 80% a nudge"). */
const NUDGE_AT = 0.8;
/** Share of the pool used at which the pool counts as nearly spent (an alert). */
const NEARLY_SPENT_AT = 0.9;

type CreditsSummary =
  | { kind: "unlimited" }
  | { kind: "metered"; used: number; total: number; remaining: number; share: number; resetAt: string | null };

export function creditsSummary(me: Me): CreditsSummary {
  if (me.creditsTotal === null || me.creditsRemaining === null) return { kind: "unlimited" };
  const total = me.creditsTotal;
  const remaining = Math.max(0, Math.min(total, me.creditsRemaining));
  const used = total - remaining;
  return { kind: "metered", used, total, remaining, share: total > 0 ? used / total : 1, resetAt: me.creditsResetAt };
}

/** What Home knows about the team (admins of a firm that can have one; null otherwise). */
export type TeamFacts = { others: number; invites: number; expiringSoon: number } | null;

/** A Home alert's way onward: a page in the panel, or AppSource in a new tab. */
type AlertAction = { label: string; to: string } | { label: string; href: string };
export type HomeAlert = { key: string; text: string; action?: AlertAction };

/**
 * Home's alerts (spec §10.2's Needs attention; A8, Dan 2026-10-02: alerts replace the
 * Get Started checklist), shown only when true, most pressing first: a failed payment, an
 * ended trial, the credit pool, invites about to expire, the add-in not yet signed in, and a
 * team nobody has been invited to. `appSourceUrl` and `envLabel` shape the sign-in alert (a
 * test build sideloads instead).
 */
export function homeAlerts(
  me: Me,
  now: Date,
  team: TeamFacts = null,
  { appSourceUrl = "", envLabel = "" }: { appSourceUrl?: string; envLabel?: string } = {},
): HomeAlert[] {
  const out: HomeAlert[] = [];
  const canBill = me.capabilities.includes("manage_billing");
  if (me.paymentFailed && canBill) {
    out.push({ key: "payment", text: "Your last payment didn't go through.", action: { label: "Update the payment method", to: "/billing" } });
  }
  const credits = creditsSummary(me);
  const addSeats = canBill && me.seats.perSeat ? { label: "Add seats", to: "/billing" } : undefined;
  if (me.trialEndsAt && new Date(me.trialEndsAt) <= now) {
    out.push({
      key: "trial",
      text: "Your trial has ended, so AI features are off until you choose a plan.",
      ...(canBill ? { action: { label: "Choose a plan", to: "/billing" } } : {}),
    });
  } else if (credits.kind === "metered" && credits.share >= NEARLY_SPENT_AT) {
    out.push({ key: "credits", text: `Your credits are nearly spent: ${credits.remaining} of ${credits.total} left.`, ...(addSeats ? { action: addSeats } : {}) });
  } else if (credits.kind === "metered" && credits.share >= NUDGE_AT && addSeats) {
    out.push({
      key: "credits",
      text: `You've used ${Math.round(credits.share * 100)}% of this period's credits. Each seat adds 1,000 credits a month.`,
      action: addSeats,
    });
  }
  if (team && team.expiringSoon > 0) {
    out.push({
      key: "expiring",
      text: team.expiringSoon === 1 ? "An invite expires within 3 days." : `${team.expiringSoon} invites expire within 3 days.`,
      action: { label: "Re-send from Team Members", to: "/members" },
    });
  }
  if (!me.pluginSignedIn) {
    out.push(
      envLabel
        ? {
            key: "signIn",
            text: `You haven't signed in to SlideBrain in PowerPoint yet. This is ${envLabel}: sideload the ${envLabel} manifest (manifest.${envLabel}.xml in the repo) instead of installing from AppSource.`,
          }
        : { key: "signIn", text: "You haven't signed in to SlideBrain in PowerPoint yet.", action: { label: "Get it from AppSource", href: appSourceUrl } },
    );
  }
  if (team && team.others === 0 && team.invites === 0) {
    out.push({ key: "invite", text: "You haven't invited any team members yet.", action: { label: "Invite your team", to: "/members" } });
  }
  return out;
}

/** Whether an admin has named the firm since sign-up gave it its placeholder. */
export const firmNamed = (me: Me): boolean => me.orgName !== PROVISIONED_FIRM_NAME;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The Plan row (Dan's frame 15187:6107): the plan, then its date in brackets: "Trial (3
 * days left)", "Team (renews Oct 29, 2026)", "Team (ends Oct 29, 2026)". */
export function planRowText(planName: string, me: Me, now: Date): string {
  if (me.planCancelsAt) return `${planName} (ends ${formatDate(me.planCancelsAt)})`;
  if (me.renewsAt) return `${planName} (renews ${formatDate(me.renewsAt)})`;
  if (me.trialEndsAt) {
    const days = Math.ceil((new Date(me.trialEndsAt).getTime() - now.getTime()) / DAY_MS);
    if (days <= 0) return `${planName} (ended)`;
    return `${planName} (${days} day${days === 1 ? "" : "s"} left)`;
  }
  return planName;
}

/** The Your Firm card's Team Members line: seats on Team, else people and invites. */
export function teamLine(seats: Me["seats"]): string {
  const pending = seats.pendingInvites;
  const invites = pending === 0 ? "" : ` · ${pending} invite${pending === 1 ? "" : "s"} pending`;
  if (seats.perSeat) return `${seats.members} of ${seats.count} seats used${invites}`;
  return `${seats.members} member${seats.members === 1 ? "" : "s"}${invites}`;
}

const DATE = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });

export function formatDate(iso: string): string {
  return DATE.format(new Date(iso));
}

/** How far through the billing period `now` is, 0–1 (Home's "Billing Period Days Used").
 * Credits reset monthly, so the period is the month before the reset. Null without a
 * reset date. */
export function billingPeriodShare(resetAt: string | null, now: Date): number | null {
  if (!resetAt) return null;
  const end = new Date(resetAt);
  const start = new Date(end);
  start.setUTCMonth(start.getUTCMonth() - 1);
  const span = end.getTime() - start.getTime();
  if (!(span > 0)) return null;
  return Math.max(0, Math.min(1, (now.getTime() - start.getTime()) / span));
}

/** The Usage card's time meter: how far through the trial (from its start to its end), else
 * through the billing period. Null when neither is known (beta, unlimited, an older API). */
export function timeMeter(me: Me, now: Date): { share: number; label: string } | null {
  if (me.trialEndsAt && me.trialStartsAt) {
    const start = new Date(me.trialStartsAt).getTime();
    const span = new Date(me.trialEndsAt).getTime() - start;
    if (!(span > 0)) return null;
    return { share: Math.max(0, Math.min(1, (now.getTime() - start) / span)), label: "Trial Days Used" };
  }
  const period = billingPeriodShare(me.creditsResetAt, now);
  return period === null ? null : { share: period, label: "Billing Period Days Used" };
}

/** The start of `now`'s month in the viewer's time zone, for "This month" (D73). */
export function monthStart(now: Date): string {
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

type BrandSection = "colors" | "typography" | "language" | "labels";
type BrandStatus = Record<BrandSection, boolean>;

/** Home's Brand Configuration card (Dan 2026-10-02): a section is Set when the firm saved any
 * of its fields. Other label rules (hidden on the page) doesn't count. */
export function brandStatus(s: FirmStandards): BrandStatus {
  const palette = s.brand?.palette;
  const fonts = s.brand?.fonts;
  return {
    colors: Boolean(palette && (palette.series?.length || palette.positive || palette.negative || palette.highlight)),
    typography: Boolean(fonts && (fonts.heading || fonts.body || fonts.headingSizePt || fonts.bodySizePt)),
    language: Boolean(s.audiencePurpose?.trim() || s.bannedWords?.length || s.languageVariant),
    labels: Boolean(s.currencyFormat || s.calendarYearFormat || s.fiscalYearFormat),
  };
}
